import * as Pipeline from '../core/utils/pipeline';
import { AdsDropdownDivider } from './components/ads-dropdown-divider/ads-dropdown-divider';
import { AdsDropdownGroup } from './components/ads-dropdown-group/ads-dropdown-group';
import { AdsDropdownItem } from './components/ads-dropdown-item/ads-dropdown-item';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { ButtonLevel, ButtonLevels, ButtonSize, ButtonSizes, Positions, Theme } from '../core/types/globalTypes';
import { Component, Element, Event, EventEmitter, Listen, Method, Prop, State, VNode, Watch, h } from '@stencil/core';
import {
  DropdownAlignment,
  DropdownBlurEvent,
  DropdownBlurHandler,
  DropdownChangeEvent,
  DropdownChangeHandler,
  DropdownErrors,
  DropdownFocusEvent,
  DropdownFocusHandler,
  DropdownItem,
  DropdownItemTypes,
  DropdownOption,
  DropdownOptionGroup,
  OpenChangeEvent,
  OpenChangeHandler,
} from './ads-dropdown.types';
import { DropdownTrigger } from './components/ads-dropdown-trigger/ads-dropdown-trigger';
import { ResizeObserver } from '@juggle/resize-observer';
import { generateId, getClassFromLevel, getClassFromSize, showErrorMessage } from '../core/utils/components';
import {
  getAllElementsWithSlotAttribute,
  getIsElementDescendantOfElement,
  getIsElementRTL,
  isNil,
  isNullOrUndefined,
  isNullOrUndefinedOrEmpty,
  setFocusOnArea,
  xor,
} from 'utils/index';

/**
 * Dropdown boxes represent mutually exclusive options in a list of text. A minimum of 6 options should be shown in the pull-down before a scroll bar is shown.
 * The contents of a dropdown box should consist of text; icons and text can be also be used. The text should be as short and concise as possible and be sentence case capitalized.
 * @example
 * <ads-dropdown data=dataObject selected-item="item-value">
 * </ads-dropdown>
 */
@Component({
  tag: 'ads-dropdown',
  styleUrl: 'ads-dropdown.scss',
  shadow: true,
})
export class AdsDropdown implements BaseComponent {
  public component: string;

  private timeout: NodeJS.Timeout;

  private ro: ResizeObserver;

  private _highlightedDropdownItemIndex: number | undefined = 0;

  constructor() {
    this.component = 'ads-dropdown';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.handleClick = this.handleClick.bind(this);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.el.setAttribute('id', this.identifier || generateId('ads-dropdown'));
  }

  /**
   * The component element
   */
  @Element() el: HTMLAdsDropdownElement;

  /**
   * Stores the internal isOpenInternal state
   * @hidden
   */
  @State() isOpen = false;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * The alignment of the dropdown list items (optional)
   * @default "left"
   */
  @Prop({ reflect: true }) alignment: DropdownAlignment = Positions.LEFT;

  /**
   * The dropdown items
   */
  @Prop() data: DropdownItem[];

  /**
   * An element that will be used as a trigger for the dropdown
   * If not specified, the default trigger will be used
   */
  @Prop() customTriggerEl?: HTMLElement;

  /**
   * The value for the aria-label, to describe the purpose of the dropdown (optional)
   */
  @Prop({ reflect: true }) srLabel?: string;

  /**
   * The level of the dropdown button: primary | secondary | tertiary (optional)
   * @default "tertiary"
   */
  @Prop({ reflect: true }) level: ButtonLevel = ButtonLevels.TERTIARY;

  /**
   * The size of the button (optional)
   * @default "default"
   */
  @Prop({ reflect: true }) size: ButtonSize = ButtonSizes.DEFAULT;

  /**
   * The dropdown blur callback function (optional)
   */
  @Prop() dropdownBlurHandler?: DropdownBlurHandler;

  /**
   * The dropdown change callback function (optional)
   */
  @Prop() dropdownChangeHandler?: DropdownChangeHandler;

  /**
   * The dropdown focus callback function (optional)
   */
  @Prop() dropdownFocusHandler?: DropdownFocusHandler;

  /**
   * Callback function for the open state of the dropdown (optional)
   */
  @Prop() openChangeHandler?: OpenChangeHandler;

  /**
   * Emits the 'dropdownBlur' custom event.
   * Usage: `document.querySelector('ads-dropdown').addEventListener('dropdownBlur', function(evt) {});`
   */
  @Event({ bubbles: false }) dropdownBlur: EventEmitter<DropdownBlurEvent>;

  /**
   * Emits the 'dropdownChange' custom event.
   * Usage: `document.querySelector('ads-dropdown').addEventListener('dropdownChange', function(evt) {});`
   */
  @Event({ bubbles: false }) dropdownChange: EventEmitter<DropdownChangeEvent>;

  /**
   * Emits the 'dropdownFocus' custom event.
   * Usage: `document.querySelector('ads-dropdown').addEventListener('dropdownFocus', function(evt) {});`
   */
  @Event({ bubbles: false }) dropdownFocus: EventEmitter<DropdownFocusEvent>;

  /**
   * Emits 'openChange' custom event.
   * Usage: `document.querySelector('ads-dropdown').addEventListener('openChange', function(evt) {});`
   */
  @Event({ bubbles: false }) openChange: EventEmitter<OpenChangeEvent>;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-dropdown'));
  }

  /**
   * Listens for the click event on 'window' to handle colapsing when clicking outside
   * @param {Event} evt The click event
   * @hidden
   */
  @Listen('click', { capture: true, target: 'window' })
  handleBodyClick(evt: Event): void {
    if (
      this.el === evt.target ||
      evt.target === this.customTriggerEl ||
      evt.composedPath()[0] === this.customTriggerEl ||
      getIsElementDescendantOfElement(evt.composedPath()[0] as Element, this.customTriggerEl)
    ) {
      return;
    }
    const isChild = getIsElementDescendantOfElement(evt.target as Element, this.el);

    if (!isChild && this.isOpen) {
      this.updateIsOpenInternal(false);
    }
  }

  /**
   * Listen to global focusout events
   * @param {Event} evt The focusout event
   */
  @Listen('focusout', { capture: true })
  handleFocusOutEvent(evt: FocusEvent): void {
    if (this.isOpen) {
      if (evt.target === this.el) {
        evt.stopPropagation();
      }
      if (
        !isNullOrUndefined(evt.relatedTarget) &&
        evt.relatedTarget !== this.el &&
        !this.el.shadowRoot.contains(evt.relatedTarget as Node) &&
        !this.el.contains(evt.relatedTarget as Node) &&
        evt.relatedTarget !== this.customTriggerEl
      ) {
        this.updateIsOpenInternal(false);
      }
    }
  }

  /**
   * Listens to 'keydown' event to handle changes in the dropdown
   * @param {Event} evt The event
   * @hidden
   */
  @Listen('keydown')
  handleClick(evt: KeyboardEvent): void {
    if (this.isOpen) {
      switch (evt.key) {
        case 'Escape':
          this.updateIsOpenInternal(false);
          break;
        case 'ArrowUp':
          evt.preventDefault();
          this.handleArrowUpDownKeyDownEvent(-1);
          break;
        case 'ArrowDown':
          evt.preventDefault();
          this.handleArrowUpDownKeyDownEvent(1);
          break;
        default:
          break;
      }
    }
  }

  /**
   * Listen to the custom 'dropdownChange' to handle closing the dropdown on selection
   * @hidden
   */
  @Listen('dropdownChange')
  handleDropdownChange(): void {
    // Close the dropdown as a result of an item being selected
    this.updateIsOpenInternal(false);
  }

  /**
   * Watch for changes on custom trigger El. Add/remove event listener for "click"
   * @param {HTMLElement} newEl The new trigger element
   * @param {HTMLElement} oldEl The old trigger element
   */
  @Watch('customTriggerEl')
  onCustomTriggerElChanged(newEl: HTMLElement, oldEl: HTMLElement): void {
    if (!isNullOrUndefined(newEl)) {
      this.addConfigurationFromTriggerEl(newEl);
    }
    if (!isNullOrUndefined(oldEl)) {
      this.removeConfigurationFromTriggerEl(oldEl);
    }
  }

  /**
   * Handle data property changes
   * @param {DropdownItem[]} newData New value for dropdown data
   */
  @Watch('data')
  handleCurrentData(newData: DropdownItem[]): void {
    if (isNullOrUndefined(newData) || isNil(newData.length)) {
      showErrorMessage(DropdownErrors.INVALID_DATA, this.el);
      return;
    }
  }

  /**
   * Opens the dropdown
   * @return {Promise<void>} Opens the dropdown
   */
  @Method()
  open(): Promise<void> {
    !this.isOpen ? this.updateIsOpenInternal(true) : null;
    return Promise.resolve();
  }

  /**
   * Closes the dropdown
   * @return {Promise<void>} Closes dropdown
   */
  @Method()
  close(): Promise<void> {
    this.isOpen ? this.updateIsOpenInternal(false) : null;
    return Promise.resolve();
  }

  /**
   * Update the isOpenInternal state with the value provided
   * @param {boolean} newValue the new value of isOpenInternal
   */
  private updateIsOpenInternal = (newValue: boolean): void => {
    this.isOpen = newValue;

    this.openChange.emit({ isOpen: newValue });
    !isNullOrUndefined(this.openChangeHandler) ? this.openChangeHandler({ isOpen: newValue }) : null;
    // focus the trigger on close

    if (!this.isOpen) {
      this._highlightedDropdownItemIndex = 0;
      this.focusTrigger();
    }
  };

  /**
   * Focus onto the trigger of the dropdown
   */
  private focusTrigger = (): void => {
    const trigger: HTMLElement = this.getTriggerElement();
    if (!isNullOrUndefined(trigger)) {
      trigger.focus();
    }
  };

  /**
   * Returns the current trigger element
   * @returns {HTMLElement} The trigger element
   */
  private getTriggerElement = (): HTMLElement => this.customTriggerEl || this.el.shadowRoot.querySelector('.c-dropdown__selector');

  /**
   * Returns the content container element
   * @returns {HTMLElement} The content of the dropdown (container)
   */
  private getContentEl = (): HTMLElement => {
    return this.el.shadowRoot.querySelector('.c-dropdown__items');
  };

  /**
   * Sanitize the proposed index value to ensure it is valid
   * @param   {number}  value   The proposed index value
   * @param   {number}  max     The maximum possible index
   * @returns {number}          The sanitized index value
   */
  private sanitizeHighlightedDropdownItemIndex = (value: number, max: number): number => {
    let _value = value;
    if (value < 0) {
      _value = 0;
    }
    if (value > max - 1) {
      _value = max - 1;
    }
    return _value;
  };

  /**
   * Handle the focusing of the next element within the dropdown list
   * @param {number} value The value indicating whether the index should increase or decrease
   */
  private handleArrowUpDownKeyDownEvent = (value: number) => {
    const dropdownItemsList = this.getContentEl();
    const dropdownItems = !isNullOrUndefined(dropdownItemsList)
      ? Array.from(dropdownItemsList.querySelectorAll('.c-dropdown__item:not(.is-disabled)'))
      : null;

    // Store the highlighted dropdown item index
    this._highlightedDropdownItemIndex =
      this._highlightedDropdownItemIndex !== undefined
        ? this.sanitizeHighlightedDropdownItemIndex((this._highlightedDropdownItemIndex += value), dropdownItems.length)
        : 0;

    // Focus the highlighted dropdown item
    dropdownItems.map(($dropdownItem: HTMLElement) => {
      const isHighlighted = this._highlightedDropdownItemIndex === dropdownItems.indexOf($dropdownItem);
      if (isHighlighted) {
        $dropdownItem.focus();
      }
    });
  };

  /**
   * Handle dropdown click events
   */
  private handleTriggerClick = (): void => {
    this.updateIsOpenInternal(!this.isOpen);
  };

  /**
   * Get the coordinates to move an element with a given height and width
   * @param  {HTMLElement} content  The content element
   * @return {number[]}             The coordinates of where the content should be placed
   */
  private getContentPosition = (content: HTMLElement): number[] => {
    const trigger = this.getTriggerElement();
    const triggerBounds = trigger.getBoundingClientRect();
    const contentParentBounds = content.parentElement.getBoundingClientRect();
    const contentBounds = content.getBoundingClientRect();
    const triggerOriginX = triggerBounds.x;
    const triggerOriginY = triggerBounds.y;
    const contentOriginsX = contentParentBounds.x;
    const contentOriginsY = contentParentBounds.y;
    const rtl = getIsElementRTL(this.el);

    // --- X OFFSET --- //
    // adjustments to the X dimension so the content fits in the screen
    const contentOriginXOffset =
      this.alignment === Positions.RIGHT
        ? -Math.min(0, triggerOriginX + triggerBounds.width - (contentBounds.width + this.getPixelsFromRem(1)))
        : Math.min(0, document.body.clientWidth - (triggerOriginX + contentBounds.width + this.getPixelsFromRem(1)));

    // --- Y OFFSET --- //
    // check if it's to low
    const expandUpward = triggerOriginY + contentBounds.height + window.pageYOffset > document.body.clientHeight;
    // calculate Y offsets
    const upwardOffset = contentBounds.height + parseInt(window.getComputedStyle(content).marginTop, 10) * 2 + triggerBounds.height;
    const yOffset = !isNullOrUndefined(this.customTriggerEl) ? triggerBounds.height : 0;
    // adjustments to the Y dimension so the content fits in the screen
    const contentOriginYAdjusted = yOffset + (expandUpward ? -upwardOffset : 0);

    // --- LTR ORIGINS --- //
    // calculations for the new X origin (before offset) are done in ltr values, then translated to rtl.
    // reason for this confusion is the conflict between rtl and alignment=right, since rtl simulates a right alignment in terms of positioning
    const triggerOriginX_Ltr = !rtl ? triggerOriginX : document.body.clientWidth - (triggerOriginX + triggerBounds.width);
    const contentOriginX_Ltr = !rtl ? contentOriginsX : document.body.clientWidth - (contentOriginsX + contentParentBounds.width);

    // match trigger X and add adjustments if any
    const newOriginX =
      // find the difference between the trigger and the content (for custom trigger)
      (triggerOriginX_Ltr -
        contentOriginX_Ltr +
        // add offset. invert for rtl
        (!rtl ? contentOriginXOffset : -contentOriginXOffset) -
        // move content left to adjust for right alignment (for custom triggers)
        (xor(this.alignment === Positions.RIGHT, rtl) ? contentBounds.width - triggerBounds.width : 0)) *
      // invert the value for rtl
      (rtl ? -1 : 1);
    // match trigger Y and add trigger height if custom trigger
    const newOriginY = triggerOriginY - contentOriginsY + contentOriginYAdjusted;
    return [newOriginX, newOriginY];
  };

  private getPixelsFromRem = (rem: number): number => rem * parseFloat(getComputedStyle(document.documentElement).fontSize);

  /**
   * Position content when dropdown is open
   * Only when a custom trigger is provided. Built-in trigger doesn't need positioning
   */
  private positionContent = () => {
    if (this.isOpen) {
      const content: HTMLElement = this.getContentEl();
      if (!isNullOrUndefined(content)) {
        content.style.setProperty('min-width', `${this.getTriggerElement().getBoundingClientRect().width}px`);
        const position = this.getContentPosition(content);
        content.style.setProperty('transform', `translate(${position[0]}px, ${position[1]}px)`);
      }
    }
  };

  /**
   * Add trigger properties to element
   * @param {HTMLElement} el The trigger element
   */
  private addConfigurationFromTriggerEl = (el: HTMLElement) => {
    el.addEventListener('click', this.handleTriggerClick);
    el.addEventListener('keydown', this.handleTriggerClick);
    el.setAttribute('aria-describedby', this.el.getAttribute('id'));
    el.setAttribute('aria-controls', this.el.getAttribute('id'));
    el.setAttribute('aria-haspopup', 'listbox');
    el.setAttribute('aria-expanded', this.isOpen.toString());
    el.style.cursor = 'pointer';
    el.tabIndex = 0;
    if (!isNullOrUndefined(this.srLabel)) {
      el.setAttribute('aria-label', this.srLabel);
    }
  };

  /**
   * Remove trigger properties to element
   * @param {HTMLElement} el The trigger element
   */
  private removeConfigurationFromTriggerEl = (el: HTMLElement) => {
    el.removeEventListener('click', this.handleTriggerClick);
    el.removeEventListener('keydown', this.handleTriggerClick);
    el.removeAttribute('aria-describedby');
    el.removeAttribute('aria-controls');
    el.removeAttribute('aria-haspopup');
    el.removeAttribute('aria-label');
    el.removeAttribute('aria-expanded');
    el.style.removeProperty('cursor');
    el.tabIndex = -1;
  };

  /**
   * Update trigger properties to element
   * @param {HTMLElement} el The trigger element
   */
  private updateConfigurationForTriggerElOnChange = (el: HTMLElement) => {
    el.setAttribute('aria-expanded', this.isOpen.toString());
  };

  protected componentWillLoad(): void {
    if (!isNullOrUndefined(this.customTriggerEl)) {
      this.addConfigurationFromTriggerEl(this.customTriggerEl);
    }
  }

  protected componentDidLoad = (): void => {
    Pipeline.addComponent(this.el);
    this.ro = new ResizeObserver(() => {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.positionContent();
      }, 100);
    });
    this.ro.observe(document.body);
  };

  protected componentDidRender(): void {
    this.positionContent();
    if (!isNullOrUndefined(this.customTriggerEl)) {
      this.updateConfigurationForTriggerElOnChange(this.customTriggerEl);
    }
    if (this.isOpen) {
      const content: HTMLElement = this.getContentEl();
      if (!isNullOrUndefined(content)) {
        setFocusOnArea(content);
      }
    }
  }

  protected disconnectedCallback = (): void => {
    Pipeline.removeComponent(this.el);
    if (!isNullOrUndefined(this.customTriggerEl)) {
      this.removeConfigurationFromTriggerEl(this.customTriggerEl);
    }
  };

  /**
   * Renders the items sub-components
   * @returns {FunctionalComponent} The item elements
   */
  private renderDropdownItems(): VNode {
    return this.data?.length > 0
      ? ((
          <div class="c-dropdown__items" role="listbox" part="items" id={`${this.identifier}:list`} aria-live={'assertive'}>
            {this.data.map((item: DropdownItem) => {
              switch (item.type) {
                case DropdownItemTypes.ACTIONGROUP:
                  return this.renderGroup(item);
                case DropdownItemTypes.DIVIDER:
                  return this.renderDivider();
                case DropdownItemTypes.ACTION:
                default:
                  return this.renderOption(item as DropdownOption);
              }
            })}
          </div>
        ) as VNode)
      : null;
  }

  /**
   * Renders a group of options
   * @param {DropdownOptionGroup} optionsGroup  The group of options to render
   * @returns {VNode}                           renders group items
   */
  private renderGroup = (optionsGroup: DropdownOptionGroup) => {
    return (
      <AdsDropdownGroup
        group={optionsGroup}
        dropdownBlur={this.dropdownBlur}
        dropdownChange={this.dropdownChange}
        dropdownFocus={this.dropdownFocus}
        dropdownBlurHandler={this.dropdownBlurHandler}
        dropdownChangeHandler={this.dropdownChangeHandler}
        dropdownFocusHandler={this.dropdownFocusHandler}
      />
    ) as VNode;
  };

  /**
   * Renders an option
   * @param {DropdownOption} option The option to render
   * @returns {VNode}               renders an option
   */
  private renderOption = (option: DropdownOption) =>
    (
      <AdsDropdownItem
        item={option}
        dropdownBlur={this.dropdownBlur}
        dropdownChange={this.dropdownChange}
        dropdownFocus={this.dropdownFocus}
        dropdownBlurHandler={this.dropdownBlurHandler}
        dropdownChangeHandler={this.dropdownChangeHandler}
        dropdownFocusHandler={this.dropdownFocusHandler}
      />
    ) as VNode;

  /**
   * Renders a dividing line
   * @returns {VNode} The divider
   */
  private renderDivider = (): VNode => (<AdsDropdownDivider />) as VNode;

  /**
   * Generate the dropdown trigger component
   * @returns {FunctionalComponent} The dropdown trigger
   */
  private renderDropdownTrigger = (): VNode => {
    const triggerClasses: string[] = [];
    triggerClasses.push(getClassFromLevel(this.level));
    triggerClasses.push(getClassFromSize(this.size));

    return (
      <DropdownTrigger
        ariaLabel={this.srLabel}
        id={this.identifier}
        modifiers={triggerClasses}
        open={this.isOpen}
        hasTriggerLabel={!isNil(getAllElementsWithSlotAttribute(this.el, 'label').length)}
        clickHandler={this.handleTriggerClick}
      />
    ) as VNode;
  };

  protected render = (): VNode => {
    const classes = ['c-dropdown'];
    classes.push(!isNullOrUndefinedOrEmpty(this.size) ? getClassFromSize(this.size) : null);
    classes.push(this.isOpen ? 'is-open' : null);

    return (
      <div class={classes.join(' ')} id={this.identifier}>
        {isNullOrUndefined(this.customTriggerEl) ? this.renderDropdownTrigger() : null}
        {this.isOpen && this.renderDropdownItems()}
      </div>
    ) as VNode;
  };
}
